import { logger } from "@/util/logger";
import React, { useEffect, useState, useCallback } from "react";
import { getAppMeta, detectPlatform } from "@/helper/helper";
import Cookies from "js-cookie";
import { PiLightningFill } from "react-icons/pi";

import {
  LATEST_VERSIONS,
  APP_STORE_URLS,
  CHECK_INTERVAL,
} from "../../constants/app_constants";

/** Mapping of raw platform strings to display values */
const PLATFORM_MAP: Record<string, "Android" | "iOS"> = {
  android: "Android",
  ios: "iOS",
};

interface UpdateAppModalProps {
  /** Optional callback when the modal is closed */
  onClose?: () => void;
}

const UpdateAppModal: React.FC<UpdateAppModalProps> = ({ onClose }) => {
  const [showModal, setShowModal] = useState(false);
  const [platform, setPlatform] = useState<"Android" | "iOS" | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  /** Helper to decide whether the modal should be shown based on the stored timestamp */
  const shouldShowModal = useCallback((currentVersion?: string) => {
    const lastCheck = Cookies.get("lastUpdateCheck");
    const now = Date.now();
    if (lastCheck) {
      const elapsed = now - parseInt(lastCheck, 10);
      if (elapsed < CHECK_INTERVAL) return false;
    }
    Cookies.set("lastUpdateCheck", now.toString());
    if (currentVersion) {
      Cookies.set("appVersion", currentVersion);
    }
    return true;
  }, []);

  useEffect(() => {
    // Only check for updates after user is logged in
    const token = localStorage.getItem("token");
    if (!token) {
      logger.log("User not logged in - skipping update check");
      return;
    }

    // Add a small delay to ensure login process is complete
    const timer = setTimeout(() => {
      checkForUpdate();
    }, 2000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkForUpdate = () => {
    const appMeta = getAppMeta();
    if (!appMeta) {
      return;
    }

    let detectedPlatform = detectPlatform();

    if (!detectedPlatform && appMeta.platform) {
      detectedPlatform = PLATFORM_MAP[appMeta.platform?.toLowerCase()] ?? null;
    }

    setPlatform(detectedPlatform);

    const currentVersion = appMeta.version;
    const latestVersionForPlatform =
      LATEST_VERSIONS[detectedPlatform as keyof typeof LATEST_VERSIONS];

    // Store the latest version for display
    setLatestVersion(latestVersionForPlatform || null);

    // If version missing, we still want to prompt the user (maybe a fresh install)
    if (!currentVersion || currentVersion.trim() === "") {
      logger.log("Version not found in appMeta - showing update modal");
      if (shouldShowModal(currentVersion)) setShowModal(true);
      return;
    }

    logger.log(
      `Current version: ${currentVersion}, Latest version: ${latestVersionForPlatform}`
    );

    const isOutdated =
      compareVersions(currentVersion, latestVersionForPlatform) < 0;
    if (!isOutdated) {
      logger.log("App is up to date");
      return;
    }

    if (shouldShowModal(currentVersion)) setShowModal(true);
  };

  const compareVersions = (current: string, latest: string): number => {
    const cur = current.split(".").map(Number);
    const lat = latest.split(".").map(Number);
    for (let i = 0; i < Math.max(cur.length, lat.length); i++) {
      const c = cur[i] ?? 0;
      const l = lat[i] ?? 0;
      if (c < l) return -1;
      if (c > l) return 1;
    }
    return 0;
  };

  const handleUpdate = () => {
    if (!platform) return;
    const storeUrl = APP_STORE_URLS[platform];
    if (!storeUrl) {
      console.warn("Store URL not defined for platform", platform);
      return;
    }
    window.open(storeUrl, "_blank");
    handleClose();
  };

  const handleClose = () => {
    setShowModal(false);
    if (onClose) onClose();
  };

  if (!showModal) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-modal-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-cardBg rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex justify-center mb-4 ">
          <span className="bg-lightPrimary p-4 rounded-full">
            <PiLightningFill className="w-16 h-16 text-primary" />
          </span>
        </div>
        {/* Title */}
        <h2
          id="update-modal-title"
          className="text-2xl font-bold text-center mb-2 text-gray-800"
        >
          Update Available
        </h2>
        {/* Description */}
        {/* {latestVersion && ( */}
        <div className="flex justify-center items-center mb-2">
          {latestVersion && (
            <p className="text-md font-medium text-gray-800 bg-gray-200 px-4 py-1 rounded-full">
              v{latestVersion}
            </p>
          )}
        </div>
        {/* )} */}
        <p className="text-center text-gray-600 mb-6">
          A new version is available with performance improvements and bug
          fixes.
        </p>
        {/* Buttons */}
        <div className="flex flex-col gap-2">
          <button
            onClick={handleUpdate}
            className="w-full px-6 py-3 bg-primary text-onPrimary rounded-lg font-semibold hover:opacity-90 transition-opacity"
          >
            Update App
          </button>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors font-medium text-center py-2"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateAppModal;
